import axios from "axios";
import { getDataRefreshTrigger } from "../DataRefreshContext";
import { queryClient } from "../queryClient";
import { rememberReturnPath } from "../post-login";

// ALWAYS use relative URLs - works for both preview and deployed versions
const API = "/api";
// Strip stray surrounding quotes. A quoted value in deploy/.env.prod
// (FRONTEND_API_KEY="br_...") gets baked in verbatim and makes every
// request send an invalid key, which 401s the whole app.
const API_KEY = (process.env.REACT_APP_API_KEY || "")
  .trim()
  .replace(/^['"]|['"]$/g, "");

// Map URL prefixes to TanStack Query keys for auto-invalidation
const RESOURCE_QUERY_KEYS = {
  "/bookings": ["bookings"],
  "/invoices": ["invoices"],
  "/enquiries": ["enquiries", "searches"],
  "/properties": ["properties"],
  "/suppliers": ["suppliers"],
  "/clients": ["clients"],
  "/quotes": ["quotes"],
  "/options": ["options"],
};

// Map URL prefixes to resource types for auto-refresh
const RESOURCE_MAP = {
  "/bookings": "bookings",
  "/invoices": "invoices",
  "/enquiries": "enquiries",
  "/properties": "properties",
  "/suppliers": "suppliers",
  "/clients": "clients",
  "/quotes": "quotes",
  "/options": "options",
};

const api = axios.create({
  baseURL: API,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    "X-API-Key": API_KEY,
  }
});

// Attach session token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("br_session_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Administrator previewing BR OS as a team member (read-only server side).
  const viewAs = localStorage.getItem("br_view_as");
  if (viewAs) {
    config.headers["X-View-As"] = viewAs;
  }
  return config;
});

// Decide whether a failed request means the user's session is dead.
//
// This used to sign the user out on ANY 401. A single unrelated 401 (a bad
// API key, one locked-down endpoint) then wiped the session and bounced the
// user to /login immediately after a successful Google login, producing an
// unbreakable login loop. Only session/auth failures may sign a user out.
const SESSION_ENDPOINTS = ["/auth/user/me", "/auth/user/logout"];

export function shouldSignOutOn401(error) {
  if (error?.response?.status !== 401) return false;
  const url = error.response.config?.url || error.config?.url || "";
  if (SESSION_ENDPOINTS.some(path => url.includes(path))) return true;
  const detail = error.response.data?.detail;
  if (typeof detail !== "string") return false;
  // Session-shaped failures only. An API-key rejection is a deploy bug,
  // not a signed-out user, so it must not clear the session.
  return /invalid or expired session|not authenticated|authentication required|missing bearer token/i.test(detail);
}

// ISS-9383f4ae: the backend refuses (409, code "looks_like_instruction") a
// supplier/client reply whose text reads like a note to the assistant, e.g.
// "translate this supplier response to english". Ask before it is emailed;
// if the user still wants it, resend once with confirm_send=true.
export const INSTRUCTION_GUARD_CODE = "looks_like_instruction";
// ISS-7f477d4e: 409 "already_replied" = we already answered their latest email
// on this thread. Show what went out; resend with confirm_followup=true only
// when the user says this reply still makes sense next to it.
export const ALREADY_REPLIED_CODE = "already_replied";

export async function handleInstructionGuard(error, { confirmFn, resend } = {}) {
  const detail = error?.response?.data?.detail;
  const config = error?.config;
  if (error?.response?.status !== 409 || !detail || !config ||
      ![INSTRUCTION_GUARD_CODE, ALREADY_REPLIED_CODE].includes(detail.code)) {
    return null;
  }
  let body = config.data;
  try { body = typeof body === "string" ? JSON.parse(body) : (body || {}); } catch { body = null; }
  const ask = confirmFn || ((msg) => window.confirm(msg));
  const send = resend || ((c) => api.request(c));
  if (detail.code === ALREADY_REPLIED_CODE) {
    if (body && !body.confirm_followup) {
      const prev = (detail.previous_replies || []).map((r) =>
        `${String(r.sent_at || "").slice(0, 16).replace("T", " ")} by ${r.sent_by || "us"}:\n"${String(r.body || "").slice(0, 300)}"`
      ).join("\n\n");
      const ok = ask(
        `We already replied to their latest email on this thread:\n\n${prev}\n\n` +
        "Your reply should read as a follow-up to that one. Send yours as it is?"
      );
      if (ok) {
        return send({ ...config, data: JSON.stringify({ ...body, confirm_followup: true }) });
      }
    }
    error.response.data = { ...error.response.data, detail: "Not sent. We already replied on this thread; edit yours into a follow-up." };
    throw error;
  }
  if (body && !body.confirm_send) {
    const preview = String(body.body || "").slice(0, 200);
    const ok = ask(
      `This text will be emailed exactly as typed:\n\n"${preview}"\n\n` +
      "It reads like a note to the assistant, not a message. Send it anyway?"
    );
    if (ok) {
      return send({ ...config, data: JSON.stringify({ ...body, confirm_send: true }) });
    }
  }
  // Not sent: give callers a plain string to show in their toast.
  error.response.data = { ...error.response.data, detail: "Not sent. It looked like a note to the assistant, not a message." };
  throw error;
}

// Global response interceptor — redirect to login on 401 + auto-refresh on mutations
api.interceptors.response.use(
  response => {
    const method = response.config.method?.toUpperCase();
    if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
      const url = response.config.url || "";
      // Skip preview/read-only POST endpoints
      if (!url.includes("/preview") && !url.includes("/lookup") && !url.includes("/sample")) {
        // Legacy DataRefreshContext trigger
        const trigger = getDataRefreshTrigger();
        if (trigger) {
          for (const [prefix, resource] of Object.entries(RESOURCE_MAP)) {
            if (url.startsWith(prefix) || url.includes(prefix)) {
              trigger(resource);
              break;
            }
          }
        }
        // TanStack Query cache invalidation
        for (const [prefix, keys] of Object.entries(RESOURCE_QUERY_KEYS)) {
          if (url.startsWith(prefix) || url.includes(prefix)) {
            keys.forEach(key => queryClient.invalidateQueries({ queryKey: [key] }));
            break;
          }
        }
      }
    }
    return response;
  },
  async error => {
    const guarded = await handleInstructionGuard(error);
    if (guarded) return guarded;
    if (shouldSignOutOn401(error) && !window.location.pathname.startsWith("/login")) {
      rememberReturnPath(window.location.pathname + window.location.search + window.location.hash);
      localStorage.removeItem("br_session_token");
      localStorage.removeItem("br_user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export { api };
export default api;
